import toast from "react-hot-toast";
import i18next from "i18next";
import {BaseClient} from "../../Api/HttpClient";
import {transformObjectToGetRequestParams} from "../../Utils/HttpHelper";
import i18n from "../../i18n";

const translateBackendError = (error, fallbackKey) => {
    const code = error?.response?.data?.code;
    if (code) {
        return i18n.t(`errors.${code}`, { defaultValue: i18n.t(fallbackKey) });
    }
    if (error?.response?.data?.message) {
        return error.response.data.message;
    }
    return i18n.t(fallbackKey);
};

export const createShiftSlice = (set, get) => ({
    shifts: [],
    shiftDetail: {},
    shiftsPaginationMeta: null,

    getMyShifts: async (dateTimestamp = null, customer_id= null, start_time = null, end_time = null, provinces = [], already_worked_for = null, branchId = null, page = null, status = null, sortBy = null, sortOrder = null, statuten = []) => {
        const params = {}
        if (dateTimestamp) {
            params.date = dateTimestamp
        }
        if (customer_id) {
            params.customer_id = customer_id
        }
        if (start_time) {
            params.start_time = start_time
        }
        if (end_time) {
            params.end_time = end_time
        }
        if (provinces.length > 0) {
            params.provinces = provinces;
        }
        if (already_worked_for) {
            params.already_worked_for = already_worked_for
        }
        if(branchId !== null && branchId !== undefined && branchId !== ''){
            params.branch_id = branchId
        }
        if(page !== null && page !== undefined){
            params.page = page
        }
        if(status !== null && status !== undefined && status !== ''){
            params.status = status
        }
        if(sortBy !== null && sortBy !== undefined && sortBy !== ''){
            params.sort_by = sortBy
        }
        if(sortOrder !== null && sortOrder !== undefined && sortOrder !== ''){
            params.sort_order = sortOrder
        }
        if (statuten && statuten.length > 0) {
            params.statuten = statuten
        }


        const user = get().user;
        const roles = user?.roles ?? [];
        const rolePath = roles.includes('admin') ? '/admin/shifts'
            : roles.includes('customer') ? '/customer/shifts'
            : '/freelancer/shifts';

        await BaseClient().get(rolePath + '?' + transformObjectToGetRequestParams(params))
            .then(response => {
                if (response.status === 200) {
                    if(response.data.data && response.data.meta) {
                        set({shifts: response.data.data, shiftsPaginationMeta: response.data.meta})
                    } else {
                        set({shifts: response.data, shiftsPaginationMeta: null})
                    }
                } else {
                    toast.error(response.data);
                }
            })
    },
    getShiftDetail: async (id) => {
        return await BaseClient().get(`/shifts/${id}`)
            .then(response => {
                if (response.status === 200) {
                    set({shiftDetail: response.data})
                    return response.data;
                } else {
                    toast.error(response.data);
                }
            })
    },
    createShift: async (data) => {
        const payload = { ...data };
        if (Array.isArray(payload.shift_repeats) && payload.start_time) {
            const startDate = String(payload.start_time).slice(0, 10);
            payload.shift_repeats = payload.shift_repeats.filter(date => date !== startDate);
        }
        try {
            const response = await BaseClient().post('/shifts', payload);
            if (response.status === 200 || response.status === 201) {
                return response.data;
            } else {
                console.log(response);
                toast.error(response.data);
            }
        } catch (error) {
            if (error?.response?.status === 400 && error?.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error(translateBackendError(error, 'errors.shift_create_failed'));
            }
            throw error;
        }
    },
    previewInterrupted: async ({ branch_id, freelancer_id, start_time, end_time }) => {
        try {
            const response = await BaseClient().post('/shifts/interrupted-preview', {
                branch_id, freelancer_id, start_time, end_time,
            });
            return !!response?.data?.interrupted;
        } catch (error) {
            return null;
        }
    },
    updateShift: async (id, data) => {
        try {
            const response = await BaseClient().patch(`/shifts/${id}`, data);
            console.log(response);
            if (response.status === 200) {
                return response.data;
            } else {
                toast.error(response.data);
            }
        } catch (error) {
            if (error?.response?.status === 400 && error?.response?.data?.message) {
                toast.error(error.response.data.message);
            } else if (error?.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error(i18next.t('shifts.toast_update_error'));
            }
            throw error;
        }
    },
    updateShiftPublished: async (id, published) => {
        await BaseClient().patch(`/shifts/${id}/published-toggle`, { published: published })
            .then(response => {
                if (response.status === 200) {
                    get().getShiftDetail(id);
                    toast.success(i18next.t('shifts.toast_publish_updated'));
                    return response.data;
                } else {
                    toast.error(response.data);
                }
            }).catch(error => {
                toast.error(i18next.t('shifts.toast_publish_error'));
                console.error(error);
            })
    },
    updateShiftTimes: async (id, data) => {
        await BaseClient().post(`/shifts/${id}/change-request`, data)
            .then(response => {
                if (response.status === 200) {
                    return response.data
                } else {
                    toast.error(response.data);
                }
            })
    },
    acceptShiftChangeRequest: async (changeRequestId, secret) => {
        await BaseClient().post(`/change-request/${changeRequestId}/accept`, {secret: secret})
            .then(response => {
                if (response.status === 200) {
                    return response.data
                } else {
                    toast.error(response.data);
                }
            })
    },
    rejectShiftChangeRequest: async (changeRequestId, secret) => {
        await BaseClient().post(`/change-request/${changeRequestId}/reject`, {secret: secret})
            .then(response => {
                if (response.status === 200) {
                    return response.data
                } else {
                    toast.error(response.data);
                }
            })
    },
    deleteShift: async (id) => {
        await BaseClient().delete(`/shifts/${id}`)
            .then(response => {
                if (response.status === 200) {
                    return response.data
                } else {
                    toast.error(response.data);
                }
            })
    },
    acceptApplication: async (shiftId, freelancerId) => {
        try {
            const response = await BaseClient().post(`/shifts/${shiftId}/freelancers/${freelancerId}/accept`);
            if (response.status === 200) {
                return response.data;
            } else {
                toast.error(response.data);
            }
        } catch (error) {
            if (error?.response?.status === 400 && error?.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error(translateBackendError(error, 'errors.shift_accept_failed'));
            }
            throw error;
        }
    },
    rejectApplication: async (shiftId, freelancerId) => {
        try {
            const response = await BaseClient().post(`/shifts/${shiftId}/freelancers/${freelancerId}/reject`);
            if (response.status === 200) {
                return response.data;
            } else {
                toast.error(response.data);
            }
        } catch (error) {
            const status = error?.response?.status;
            if (status === 404) {
                toast.error(i18next.t('applications_module.errors.application_not_found'));
            } else if (status === 400 && error?.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error(translateBackendError(error, 'errors.shift_reject_failed'));
            }
            throw error;
        }
    },
    withdrawShift: async (shiftId) => {
        try {
            const response = await BaseClient().post(`/shifts/${shiftId}/withdraw`);
            if (response.status === 200) {
                toast.success(i18next.t('shifts_module.toast_withdraw_success'));
                const shifts = get().shifts;
                const index = shifts.findIndex(shift => shift?.id === shiftId);
                if (index !== -1) {
                    shifts[index] = response.data;
                    set({shifts: [...shifts]});
                }
                if (get().shiftDetail?.id === shiftId) {
                    set({shiftDetail: response.data});
                }
                return true;
            }
            return false;
        } catch (error) {
            if (error?.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error(i18next.t('shifts_module.toast_withdraw_error'));
            }
            return false;
        }
    },

    getShiftsForUser: async (userId,
                             page = 1,
                             startTime = null,
                             endTime = null,
                             sortBy = null,
                             sortOrder = null,
                             branchId = null,
                             nopagination = false
    ) => {
        try {
            const params = new URLSearchParams();
            params.append('page', page);

            if (startTime) {
                params.append('start_time', startTime);
            }
            if (endTime) {
                params.append('end_time', endTime);
            }
            if (sortBy) {
                params.append('sort_by', sortBy);
            }
            if (sortOrder) {
                params.append('sort_order', sortOrder);
            }
            if (branchId) {
                params.append('branch_id', branchId);
            }
            if(nopagination){
                params.append('nopagination', '1');
            }

            const response = await BaseClient().get(`/users/${userId}/shifts?${params.toString()}`);
            if (response.status === 200) {
                if(nopagination){
                    return {data: response.data};
                }

                return {
                    data: response.data?.data || [],
                    meta: response.data?.meta || {}
                };
            } else {
                toast.error(response.data);
                return { data: [], meta: {} };
            }
        } catch (error) {
            console.error('Error fetching shifts for user:', error);
            toast.error(i18next.t('general.errors.generic'));
            return { data: [], meta: {} };
        }
    }
})